import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../api/products';
import { categoriesAPI } from '../api/categories';
import { ProductDetail, Category } from '../types/product';
import ProductGrid from '../components/Products/ProductGrid';
import ProductList from '../components/Products/ProductList';
import Breadcrumb from '../components/Layout/Breadcrumb';
import SearchBar from '../components/Search/SearchBar';
import { FiFilter, FiGrid, FiList } from 'react-icons/fi';
import { getLocalizedField } from '../utils/localize';
import type { SupportedLanguage } from '../i18n';

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: calc(var(--header-height) + 1rem);
  padding-bottom: 2rem;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;


const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: var(--white);
  border: 1px solid var(--gray-medium);
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: var(--gray-light);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ViewButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background-color: ${props => props.$active ? 'var(--primary-color)' : 'var(--white)'};
  color: ${props => props.$active ? 'var(--white)' : 'var(--text-color)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--gray-medium)'};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.$active ? 'var(--secondary-color)' : 'var(--gray-light)'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ProductsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ProductDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);

  const categoryId = searchParams.get('category');
  const searchQuery = searchParams.get('search') || '';

  useEffect(() => {
    const loadCategoryInfo = async () => {
      if (categoryId) {
        try {
          const category = await categoriesAPI.getCategory(parseInt(categoryId));
          setCurrentCategory(category);
        } catch (error) {
          console.error('Failed to load category:', error);
        }
      } else {
        setCurrentCategory(null);
      }
    };

    loadCategoryInfo();
  }, [categoryId]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const params = {
          search: searchQuery,
          ...(categoryId && { category_id: parseInt(categoryId) })
        };
        const data = await productsAPI.getProducts(params);
        setProducts(data);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [searchQuery, categoryId]);

  const breadcrumbItems = [
    { label: t('common.home'), path: '/' },
    ...(currentCategory
      ? [
          { label: t('breadcrumb.categories'), path: '/categories' },
          { label: getLocalizedField(currentCategory, 'name', language) as string }
        ]
      : [{ label: t('breadcrumb.products') }]
    )
  ];

  return (
    <PageContainer>
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <PageTitle>
            {currentCategory ? (getLocalizedField(currentCategory, 'name', language) as string) : t('common.ourProducts')}
          </PageTitle>
        </PageHeader>

        <FilterBar>
          <SearchBar
            onSearch={(query) => {
              const params = new URLSearchParams(searchParams);
              if (query) {
                params.set('search', query);
              } else {
                params.delete('search');
              }
              setSearchParams(params);
            }}
          />

          <FilterButton>
            <FiFilter />
            <span>{t('products.filter')}</span>
          </FilterButton>

          <ViewToggle>
            <ViewButton
              $active={viewMode === 'grid'}
              onClick={() => setViewMode('grid')}
              aria-label={t('products.gridView')}
              data-testid="view-toggle-grid"
            >
              <FiGrid />
            </ViewButton>
            <ViewButton
              $active={viewMode === 'list'}
              onClick={() => setViewMode('list')}
              aria-label={t('products.listView')}
              data-testid="view-toggle-list"
            >
              <FiList />
            </ViewButton>
          </ViewToggle>
        </FilterBar>

        {viewMode === 'grid' ? (
          <ProductGrid products={products} loading={loading} />
        ) : (
          <ProductList products={products} loading={loading} />
        )}
      </div>
    </PageContainer>
  );
};

export default ProductsPage;